import React, { useState, useRef, useEffect } from 'react'
import { HelpCircle, X, Send, Mail, Phone, User, CheckCircle } from 'lucide-react'
import apiClient from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { toastService } from '@/hooks/useToast'

export function HelpButton() {
  const { user } = useAuthStore()
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  async function handleSend() {
    if (!message.trim()) return
    setSending(true)
    try {
      // Step 1: Find the super admin user to assign the task to
      const usersRes = await apiClient.get('/users?size=20')
      const allUsers: any[] = usersRes.data?.data ?? usersRes.data ?? []
      const admin = allUsers.find((u: any) => {
        const roles: string[] = (u.roles ?? []).map((r: string) => r.toLowerCase())
        return roles.includes('super_admin') || roles.includes('org_admin')
      })

      const from = user?.fullName || user?.firstName || 'A user'
      const role = user?.role?.name?.replace(/_/g, ' ') || 'User'

      // Step 2: Create a task assigned to admin — triggers automatic notification bell
      await apiClient.post('/tasks', {
        title: `[Support] ${from} (${role}) needs help`,
        description: message.trim(),
        status: 'pending',
        priority: 'high',
        assignedTo: admin?.id || undefined,
      })

      setSent(true)
      setMessage('')
      toastService.success('Your message has been sent to the admin')
      setTimeout(() => { setSent(false); setOpen(false) }, 2500)
    } catch {
      toastService.error('Failed to send message. Please contact admin directly.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div ref={popoverRef} className="fixed bottom-6 right-6 z-40 hidden lg:block">
      {/* Popover */}
      {open && (
        <div className="absolute bottom-14 right-0 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-mist dark:border-slate-700 overflow-hidden animate-fade-in">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-mist dark:border-slate-700"
            style={{ background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-600, #1d4ed8) 100%)' }}>
            <div className="flex items-center gap-2">
              <HelpCircle size={16} className="text-white" />
              <p className="text-sm font-bold text-white">Help & Support</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-md hover:bg-white/20 text-white transition-colors">
              <X size={14} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Admin Contact Card */}
            <div className="bg-cloud/50 dark:bg-slate-700/40 rounded-xl p-3">
              <p className="text-[10px] font-semibold text-steel dark:text-slate-400 uppercase tracking-wider mb-2">Your Admin Contact</p>
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <User size={16} style={{ color: 'var(--accent)' }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-navy dark:text-slate-100">Radhika Y</p>
                  <p className="text-[10px] text-steel dark:text-slate-400 mb-1.5">Super Admin · Artech Solutions</p>
                  <div className="space-y-1">
                    <a href="mailto:radhika.y@artechsolution.co.in"
                      className="flex items-center gap-1.5 text-[11px] text-accent hover:underline">
                      <Mail size={10} />
                      radhika.y@artechsolution.co.in
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Report Issue Form */}
            {sent ? (
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <CheckCircle size={28} className="text-green-500 mb-2" />
                <p className="text-xs font-semibold text-navy dark:text-slate-100">Message Sent!</p>
                <p className="text-[11px] text-steel dark:text-slate-400 mt-0.5">Admin will get back to you shortly</p>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-navy dark:text-slate-200 mb-1.5">
                  Describe your issue
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="e.g. I can't add a contact, the export isn't working..."
                  rows={3}
                  className="w-full text-xs border border-mist dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-navy dark:text-slate-100 placeholder-steel/50 dark:placeholder-slate-500 outline-none focus:border-accent resize-none transition-colors"
                />
                <button
                  onClick={handleSend}
                  disabled={!message.trim() || sending}
                  className="mt-2 w-full py-2 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50 transition-all active:scale-95"
                  style={{ backgroundColor: 'var(--accent)' }}
                >
                  {sending ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send size={12} />
                  )}
                  {sending ? 'Sending...' : 'Send to Admin'}
                </button>
              </div>
            )}

            <p className="text-[10px] text-steel/60 dark:text-slate-500 text-center">
              Your message will be delivered as an in-app notification
            </p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => { setOpen(o => !o); setSent(false) }}
        className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center text-white transition-all hover:shadow-xl active:scale-95 hover:scale-105"
        style={{ backgroundColor: 'var(--accent)' }}
        title="Help & Support"
      >
        {open ? <X size={18} /> : <HelpCircle size={20} />}
      </button>
    </div>
  )
}

export default HelpButton
